import classes from "./classes.module.scss";
import Button, { EButtonColor, EButtonSize, EButtonVariant } from "components/elements/Button";
import Typography from "components/elements/Typography";
import I18n from "components/materials/I18n";
import DefaultAvatar from "assets/images/default-avatar.svg";
import { ArrowRightIcon } from "@heroicons/react/20/solid";
import { Link } from "react-router-dom";
import { container } from "tsyringe";
import ModuleConfig from "configs/ModuleConfig";
import LazyImage from "components/elements/LazyImage";
import ProjectResponseResource from "common/resources/Project/ProjectResponseResource";
import { formatDate } from "utils/DateUtils";
import { match } from "ts-pattern";
import { useCallback, useEffect, useState } from "react";
import ConfigService from "services/projects/ConfigService";
import ContributorService from "services/projects/ContributorService";
import ContributorResponseResource from "common/resources/Project/Config/Contributor/ContributorResponseResource";
import { usePoster } from "components/pages/Project/ProjectTemplate/hooks/usePoster";

import ProjectConfigResponseResource from "common/resources/Project/Config/ProjectConfig/ProjectConfigResponseResource";
import { translateISO369 } from "utils/I18nText";
import ContributorBadge from "components/pages/ProfessionalSpace/components/ContributorBadge";
const modules = container.resolve(ModuleConfig).get().modules;
const contributorService = container.resolve(ContributorService);
const configService = container.resolve(ConfigService);

type IProps = {
	project: ProjectResponseResource;
};

function getDescription(description: string) {
	const maxLength = 222;
	return description.length > maxLength ? description.slice(0, maxLength) + "..." : description;
}

export default function ProjectCard(props: IProps) {
	const { projectLeader, projectLeaderAvatar } = useProjectLeader(props.project);
	const { poster } = usePoster(props.project);
	const { projectConfig } = useProjectConfig(props.project);

	const projectId = props.project.id;
	const publishedAt = formatDate(I18n.getLocale(), new Date(props.project.publishedAt ?? props.project.createdAt ?? ""));
	const theme = I18n.trslt(I18n.asset.enums.EProjectThemes[props.project.theme]);
	const format = I18n.trslt(I18n.asset.enums.EProjectFormats[props.project.format]);
	const title = match(I18n.getLang())
		.with("fr", () => props.project.title)
		.otherwise(() => props.project.title_en);

	return (
		<div className={classes["root"]}>
			<div className={classes["poster-container"]}>
				<LazyImage src={poster} alt="Project poster" className={classes["poster-image"]} />
			</div>

			<div className={classes["body-container"]}>
				{poster && <LazyImage src={poster} alt="" className={classes["background"]} />}

				<div className={classes["body"]}>
					<div className={classes["header"]}>
						{projectLeader && projectLeaderAvatar && (
							<div className={classes["author"]}>
								<Typography type="p" weight="medium" size="small" color="white">
									{I18n.trslt(I18n.asset.component.project_card.project_leader)}
								</Typography>

								<ContributorBadge contributor={projectLeader} avatarUrl={projectLeaderAvatar} color="white" />
							</div>
						)}

						<div className={classes["infos"]}>
							<div className={classes["info"]}>
								<Typography type="p" size="medium" weight="medium" color="white">
									{I18n.trslt(I18n.asset.component.featured_project.published_at, { var1: publishedAt })}
								</Typography>
							</div>

							<div className={classes["info"]}>
								<Typography type="p" size="medium" weight="medium" color="white">
									{I18n.trslt(I18n.asset.component.featured_project.category)}
									<Typography type="span" weight="medium" className={classes["category"]} color="white">
										{theme}
									</Typography>
								</Typography>
							</div>

							<div className={classes["info"]}>
								<Typography type="p" size="medium" weight="medium" color="white">
									{I18n.trslt(I18n.asset.component.featured_project.length)} {format}
								</Typography>
							</div>
						</div>
					</div>

					<div className={classes["content"]}>
						<Typography type="h2" weight="bold" color="white">
							{title}
						</Typography>

						{projectConfig?.description && (
							<Typography type="p" weight="regular" size="medium" color="white">
								{getDescription(translateISO369(projectConfig.description) ?? "")}
							</Typography>
						)}
					</div>
				</div>

				<Link to={modules.pages.Project.props.path.replace(":projectId", projectId)}>
					<Button variant={EButtonVariant.CONTAINED} color={EButtonColor.PRIMARY} size={EButtonSize.LARGE} endIcon={<ArrowRightIcon />}>
						{I18n.trslt(I18n.asset.component.featured_project.button)}
					</Button>
				</Link>
			</div>
		</div>
	);
}

export function useProjectLeader(project: Pick<ProjectResponseResource, "id" | "projectTeam">) {
	const [projectLeader, setProjectLeader] = useState<ContributorResponseResource | null>(null);
	const [projectLeaderAvatar, setProjectLeaderAvatar] = useState<string | null>(null);

	const clearProjectLeaderAvatar = useCallback(() => {
		setProjectLeaderAvatar(DefaultAvatar);
	}, []);

	const fetchProjectLeaderAvatar = useCallback(() => {
		if (!project.projectTeam?.projectLeader?.avatar) return clearProjectLeaderAvatar();

		return contributorService.downloadAvatar(project.id, project.projectTeam.id, project.projectTeam.projectLeader.id).then((avatar) => {
			if (!avatar) return clearProjectLeaderAvatar();
			return setProjectLeaderAvatar(URL.createObjectURL(avatar));
		});
	}, [clearProjectLeaderAvatar, project]);

	useEffect(() => {
		if (!project.projectTeam?.projectLeader) return;
		setProjectLeader(project.projectTeam.projectLeader);
		fetchProjectLeaderAvatar();
	}, [project, fetchProjectLeaderAvatar]);

	return { projectLeader, projectLeaderAvatar };
}

export const useProjectConfig = (project: Pick<ProjectResponseResource, "id">) => {
	const [projectConfig, setProjectConfig] = useState<ProjectConfigResponseResource | null>(null);

	const fetchProjectConfig = useCallback(async () => {
		configService.getByProjectId(project.id).then(setProjectConfig).catch(console.error);
	}, [project]);

	useEffect(() => {
		fetchProjectConfig();
	}, [fetchProjectConfig]);

	return { projectConfig };
};
